import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { CHARACTERS, DISCIPLINES, type DisciplineSlug } from "@jumpbean/shared";
import { Button, CharacterImage, Card } from "../components";
import { usePageTitle } from "../hooks";

// ==================== Icons ====================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChildIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    </svg>
  );
}

// ==================== Types ====================

interface FloatingParticle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
  type: "star" | "sparkle" | "circle";
}

interface FAQItem {
  question: string;
  answer: string;
}

// ==================== Data ====================

const PARTICLE_COLORS: string[] = [
  "#FFD93D", // sunshine
  "#E8A0FF", // luna
  "#90EE90", // petal
  "#74B9FF", // prism blue
  "#FF6B9D", // fairy pink
  "#FF8C42", // pixel
];

const PARTICLE_TYPES: FloatingParticle["type"][] = ["star", "sparkle", "circle"];

function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)] as T;
}

const HERO_PARTICLES: FloatingParticle[] = Array.from({ length: 30 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 12 + 4,
  color: getRandomItem(PARTICLE_COLORS),
  delay: Math.random() * 5,
  duration: Math.random() * 3 + 4,
  type: getRandomItem(PARTICLE_TYPES),
}));

const DISCIPLINE_DETAILS: Record<DisciplineSlug, { tagline: string; skills: string[] }> = {
  math: {
    tagline: "Count stars with Luna in her magical castle",
    skills: ["Counting 1-20", "Number recognition", "Basic addition", "Simple subtraction"],
  },
  spelling: {
    tagline: "Grow word gardens with Petal the Fairy",
    skills: ["Letter recognition", "Phonics basics", "Beginning sounds", "3-letter words"],
  },
  patterns: {
    tagline: "Build rainbow bridges with Prism the Dragon",
    skills: ["Color patterns", "Shape sequences", "Size patterns", "Complex sequences"],
  },
  typing: {
    tagline: "Cast keyboard spells with Whiskers",
    skills: ["Key finding", "Letter sequences", "Simple words", "Short phrases"],
  },
  programming: {
    tagline: "Code adventures with Pixel the Robot Owl",
    skills: ["Direction commands", "Sequences", "Maze solving", "Basic loops"],
  },
};

const FAQ_DATA: FAQItem[] = [
  {
    question: "Is JumpBean really free?",
    answer:
      "Level 1 of all five disciplines is completely free, forever. That's 50 challenges to see if JumpBean is right for your family. No credit card required, no trial that auto-converts.",
  },
  {
    question: "What ages is JumpBean designed for?",
    answer:
      "JumpBean is designed specifically for 4-5 year olds (preschool age). The touch targets, session lengths, audio instructions, and difficulty progression are all built for this developmental stage.",
  },
  {
    question: "Will my child see ads or be asked to buy things?",
    answer:
      "Never. Your child's experience is completely ad-free and purchase-free. All billing happens in your parent account, separate from their play experience.",
  },
  {
    question: "What if my child gets frustrated?",
    answer:
      "They won't! JumpBean uses positive-only feedback. There are no 'wrong answer' punishments—just gentle encouragement to try again. Our magical guides celebrate effort, not just success.",
  },
  {
    question: "How much screen time does JumpBean require?",
    answer:
      "Each challenge takes 2-5 minutes. A typical session might be 10-20 minutes. You control how much time your child spends—JumpBean doesn't use manipulative tactics to keep them glued to the screen.",
  },
  {
    question: "Can I cancel anytime?",
    answer:
      "Yes. No contracts, no cancellation fees. If JumpBean isn't right for your family, you can cancel with one click.",
  },
];

// ==================== Components ====================

/** Floating particle for hero background */
function Particle({ particle }: { particle: FloatingParticle }) {
  const shapes = {
    star: (
      <svg viewBox="0 0 24 24" fill={particle.color} className="w-full h-full">
        <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
      </svg>
    ),
    sparkle: (
      <svg viewBox="0 0 24 24" fill={particle.color} className="w-full h-full">
        <circle cx="12" cy="12" r="10" opacity="0.8" />
        <circle cx="12" cy="12" r="6" fill="white" opacity="0.5" />
      </svg>
    ),
    circle: (
      <div
        className="w-full h-full rounded-full"
        style={{ backgroundColor: particle.color, opacity: 0.6 }}
      />
    ),
  };

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${particle.x}%`,
        top: `${particle.y}%`,
        width: particle.size,
        height: particle.size,
        animation: `floatParticle ${particle.duration}s ease-in-out ${particle.delay}s infinite`,
      }}
    >
      {shapes[particle.type]}
    </div>
  );
}

/** Hero section with animated characters and particles */
function HeroSection() {
  const [activeCharacter, setActiveCharacter] = useState<DisciplineSlug>("math");

  useEffect(() => {
    const disciplines: DisciplineSlug[] = ["math", "spelling", "patterns", "typing", "programming"];
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % disciplines.length;
      const nextDiscipline = disciplines[index];
      if (nextDiscipline) {
        setActiveCharacter(nextDiscipline);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-magic-100 via-sunshine-50 to-sky-100" />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {HERO_PARTICLES.map((particle) => (
          <Particle key={particle.id} particle={particle} />
        ))}
      </div>

      {/* Large decorative shapes */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-luna/20 blur-3xl animate-pulse" />
      <div className="absolute bottom-40 right-20 w-48 h-48 rounded-full bg-petal/20 blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      <div className="absolute top-1/2 left-1/4 w-24 h-24 rounded-full bg-sunshine/30 blur-2xl animate-pulse" style={{ animationDelay: "2s" }} />

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Text content */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6 shadow-playful">
              <span className="text-lg">✨</span>
              <span className="font-body text-body-sm font-semibold text-neutral-700">
                Designed for ages 4-5
              </span>
            </div>

            {/* Main headline */}
            <h1 className="font-display font-extrabold text-display-lg md:text-display-xl text-neutral-900 mb-6 leading-tight">
              Screen time that
              <span className="relative inline-block mx-2">
                <span className="relative z-10 text-magic-600">teaches</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="12"
                  viewBox="0 0 200 12"
                  fill="none"
                >
                  <path
                    d="M2 8C50 2 150 2 198 8"
                    stroke="#FFD93D"
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="text-sunshine-600">.</span>
              <br />
              <span className="text-magic-500">Finally.</span>
            </h1>

            {/* Subheadline */}
            <p className="font-body text-body-lg md:text-body-xl text-neutral-600 mb-8 max-w-xl mx-auto lg:mx-0">
              JumpBean helps your 4-5 year old master{" "}
              <span className="font-semibold text-luna-dark">math</span>,{" "}
              <span className="font-semibold text-petal-dark">spelling</span>,{" "}
              <span className="font-semibold text-prism-blue">patterns</span>,{" "}
              <span className="font-semibold text-whiskers-dark">typing</span>, and{" "}
              <span className="font-semibold text-pixel-dark">coding basics</span>—through
              play, not pressure.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link to="/register">
                <Button size="lg" variant="primary" className="group">
                  <span>Start Free</span>
                  <span className="ml-2 transition-transform group-hover:translate-x-1">→</span>
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button size="lg" variant="secondary">
                  See How It Works
                </Button>
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-8 text-neutral-500">
              <div className="flex items-center gap-2">
                <span className="text-meadow-500 text-lg">✓</span>
                <span className="font-body text-body-sm">No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-meadow-500 text-lg">✓</span>
                <span className="font-body text-body-sm">No ads, ever</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-meadow-500 text-lg">✓</span>
                <span className="font-body text-body-sm">COPPA compliant</span>
              </div>
            </div>
          </div>

          {/* Right: Character showcase */}
          <div className="relative order-1 lg:order-2 flex justify-center">
            {/* Character carousel background */}
            <div className="relative w-[400px] h-[400px] md:w-[550px] md:h-[550px]">
              {/* Glow ring */}
              <div
                className="absolute inset-0 rounded-full transition-all duration-500"
                style={{
                  background: `radial-gradient(circle, ${
                    activeCharacter === "math"
                      ? "rgba(232,160,255,0.4)"
                      : activeCharacter === "spelling"
                        ? "rgba(144,238,144,0.4)"
                        : activeCharacter === "patterns"
                          ? "rgba(116,185,255,0.4)"
                          : activeCharacter === "typing"
                            ? "rgba(74,144,217,0.4)"
                            : "rgba(255,140,66,0.4)"
                  } 0%, transparent 70%)`,
                }}
              />

              {/* Main character */}
              <div className="absolute inset-0 flex items-center justify-center">
                <CharacterImage
                  discipline={activeCharacter}
                  expression="happy"
                  size="hero-xl"
                  floating
                  glowing
                />
              </div>

              {/* Orbiting mini characters */}
              {(["math", "spelling", "patterns", "typing", "programming"] as DisciplineSlug[]).map(
                (discipline, index) => {
                  if (discipline === activeCharacter) return null;
                  const angle = (index * 72 - 90) * (Math.PI / 180);
                  const radius = 240;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  return (
                    <button
                      key={discipline}
                      onClick={() => setActiveCharacter(discipline)}
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-300 hover:scale-110 cursor-pointer"
                      style={{
                        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                      }}
                    >
                      <div className="relative">
                        <CharacterImage
                          discipline={discipline}
                          expression="base"
                          size="sm"
                          className="opacity-80 hover:opacity-100"
                        />
                        {/* Tooltip */}
                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity">
                          <span className="font-body text-body-xs bg-white/90 px-2 py-1 rounded-full shadow-sm">
                            {DISCIPLINES[discipline].name}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                }
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-neutral-400 flex justify-center pt-2">
          <div className="w-1 h-3 bg-neutral-400 rounded-full animate-scroll" />
        </div>
      </div>
    </section>
  );
}

/** Problem/Solution section */
function ProblemSection() {
  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-rainbow" />

      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-display font-bold text-display-md text-neutral-800 mb-4">
            We get it. Screen time is{" "}
            <span className="relative inline-block">
              complicated
              <span className="absolute bottom-0 left-0 w-full h-2 bg-fairy-200 -z-10 rotate-[-1deg]" />
            </span>
            .
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            {
              icon: "/images/icons/icon-overwhelming.png",
              title: "Overwhelming",
              description: "Endless menus, aggressive upsells, features everywhere",
              color: "bg-fairy-50 border-fairy-200",
            },
            {
              icon: "/images/icons/icon-boring.png",
              title: "Boring",
              description: "Digital worksheets with cartoon wrappers",
              color: "bg-sky-50 border-sky-200",
            },
            {
              icon: "/images/icons/icon-passive.png",
              title: "Passive",
              description: "YouTube in disguise—watching, not learning",
              color: "bg-sunshine-50 border-sunshine-200",
            },
          ].map((item) => (
            <div
              key={item.title}
              className={`${item.color} border-2 rounded-2xl p-6 text-center transition-transform hover:-translate-y-1`}
            >
              <img
                src={item.icon}
                alt={item.title}
                className="w-16 h-16 mx-auto mb-3 object-contain"
              />
              <h3 className="font-display font-bold text-body-lg text-neutral-800 mb-2">
                {item.title}
              </h3>
              <p className="font-body text-body-md text-neutral-600">{item.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="font-body text-body-lg text-neutral-700 max-w-2xl mx-auto">
            You want something different. Something that{" "}
            <span className="font-semibold">actually teaches</span>. Something they'll{" "}
            <span className="font-semibold">ask for again</span>. Something you can{" "}
            <span className="font-semibold text-meadow-600">feel good about</span>.
          </p>
        </div>
      </div>
    </section>
  );
}

/** Solution intro section */
function SolutionSection() {
  return (
    <section className="py-20 bg-gradient-to-b from-magic-50 to-white relative overflow-hidden">
      {/* Floating decorations */}
      <div className="absolute top-20 right-10 w-16 h-16 text-sunshine-400 animate-float opacity-50">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
        </svg>
      </div>
      <div
        className="absolute bottom-20 left-10 w-6 h-6 text-fairy-300 animate-float opacity-30 blur-[1px]"
        style={{ animationDelay: "1s" }}
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="10" />
        </svg>
      </div>

      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-block font-display font-bold text-body-lg text-magic-500 mb-4">
            Enter JumpBean
          </span>
          <h2 className="font-display font-extrabold text-display-md md:text-display-lg text-neutral-900 mb-6">
            Learning that feels like{" "}
            <span className="text-magic-600">magic</span>
          </h2>
          <p className="font-body text-body-lg text-neutral-600 max-w-2xl mx-auto">
            Instead of lectures or worksheets, your child goes on adventures with five
            magical character guides. Each one is a friend, not a teacher.
          </p>
        </div>

        {/* Key differentiators */}
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              image: "/images/icons/icon-no-wrong-answers.png",
              title: "No Wrong Answers",
              description:
                "Every attempt gets encouragement. No punishments, no red X marks—just sparkles and 'try again!'",
              color: "from-sunshine-100 to-sunshine-50",
            },
            {
              image: "/images/icons/icon-no-ads.png",
              title: "No Ads. Ever.",
              description:
                "Your child never sees paywalls, popups, or purchase prompts. All billing is parent-only.",
              color: "from-meadow-100 to-meadow-50",
            },
            {
              image: "/images/icons/icon-preschool-sessions.png",
              title: "Preschool-Sized Sessions",
              description:
                "2-5 minute challenges designed for preschool attention spans. Perfect for before dinner or quiet time.",
              color: "from-sky-100 to-sky-50",
            },
          ].map((item) => (
            <div
              key={item.title}
              className={`bg-gradient-to-br ${item.color} rounded-3xl p-8 shadow-playful transition-all duration-300 hover:shadow-playful-lg hover:-translate-y-1`}
            >
              <div className="w-24 h-24 mx-auto mb-4">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-contain drop-shadow-md"
                />
              </div>
              <h3 className="font-display font-bold text-body-xl text-neutral-900 mb-3">
                {item.title}
              </h3>
              <p className="font-body text-body-md text-neutral-700">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Character showcase section */
function CharacterSection() {
  const [hoveredDiscipline, setHoveredDiscipline] = useState<DisciplineSlug | null>(null);

  return (
    <section id="characters" className="py-20 bg-white relative z-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-4">
            Meet your child's new{" "}
            <span className="text-fairy-500">learning friends</span>
          </h2>
          <p className="font-body text-body-lg text-neutral-600 max-w-xl mx-auto">
            Five magical guides ready to make learning an adventure
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {(Object.entries(DISCIPLINES) as [DisciplineSlug, typeof DISCIPLINES.math][]).map(
            ([slug, discipline]) => {
              const character = Object.values(CHARACTERS).find((c) => c.discipline === slug);
              const isHovered = hoveredDiscipline === slug;

              return (
                <div
                  key={slug}
                  className="group relative"
                  onMouseEnter={() => setHoveredDiscipline(slug)}
                  onMouseLeave={() => setHoveredDiscipline(null)}
                >
                  <div
                    className={`
                      relative bg-white rounded-3xl p-6 border-3 transition-all duration-300
                      ${isHovered ? "border-current shadow-playful-lg -translate-y-2" : "border-neutral-200 shadow-playful"}
                    `}
                    style={{
                      borderColor: isHovered
                        ? slug === "math"
                          ? "#E8A0FF"
                          : slug === "spelling"
                            ? "#90EE90"
                            : slug === "patterns"
                              ? "#74B9FF"
                              : slug === "typing"
                                ? "#4A90D9"
                                : "#FF8C42"
                        : undefined,
                    }}
                  >
                    {/* Character image */}
                    <div className="relative mb-4 flex justify-center">
                      <CharacterImage
                        discipline={slug}
                        expression={isHovered ? "happy" : "base"}
                        size="xl"
                        floating={isHovered}
                        glowing={isHovered}
                      />
                    </div>

                    {/* Character name */}
                    <h3 className="font-display font-bold text-body-lg text-neutral-900 text-center mb-1">
                      {character?.name.split(" ")[0]}
                    </h3>

                    {/* Discipline name */}
                    <p className="font-body text-body-sm text-neutral-500 text-center">
                      {discipline.name}
                    </p>

                    {/* Hover tooltip */}
                    <div
                      className={`
                        absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-full
                        w-64 bg-white rounded-2xl shadow-playful-lg p-4 z-20
                        transition-all duration-300 origin-top
                        ${isHovered ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}
                      `}
                    >
                      <p className="font-body text-body-sm text-neutral-700 mb-2">
                        {DISCIPLINE_DETAILS[slug].tagline}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {DISCIPLINE_DETAILS[slug].skills.slice(0, 2).map((skill) => (
                          <span
                            key={skill}
                            className="inline-block bg-neutral-100 rounded-full px-2 py-0.5 text-body-xs text-neutral-600"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    </section>
  );
}

/** Disciplines detail section */
function DisciplinesSection() {
  return (
    <section className="py-20 bg-gradient-to-b from-sunshine-50 to-white relative z-0">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-4">
            Five ways to get{" "}
            <span className="text-sunshine-600">kindergarten ready</span>
          </h2>
          <p className="font-body text-body-lg text-neutral-600 max-w-xl mx-auto">
            Each discipline builds essential skills through play-based challenges
          </p>
        </div>

        <div className="space-y-8">
          {(Object.entries(DISCIPLINES) as [DisciplineSlug, typeof DISCIPLINES.math][]).map(
            ([slug, discipline], index) => {
              const isEven = index % 2 === 0;
              const details = DISCIPLINE_DETAILS[slug];
              const gradients: Record<DisciplineSlug, string> = {
                math: "from-luna-light/30 to-luna/10",
                spelling: "from-petal-light/30 to-petal/10",
                patterns: "from-prism-blue/20 to-prism-purple/10",
                typing: "from-whiskers-light/30 to-whiskers/10",
                programming: "from-pixel-light/30 to-pixel/10",
              };

              return (
                <div
                  key={slug}
                  className={`
                    flex flex-col ${isEven ? "md:flex-row" : "md:flex-row-reverse"}
                    items-center gap-8 md:gap-12
                    bg-gradient-to-br ${gradients[slug]}
                    rounded-3xl p-8 md:p-12
                  `}
                >
                  {/* Character */}
                  <div className="flex-shrink-0">
                    <CharacterImage
                      discipline={slug}
                      expression="pointing"
                      size="2xl"
                      floating
                      glowing
                    />
                  </div>

                  {/* Content */}
                  <div className={`flex-1 ${isEven ? "md:text-left" : "md:text-right"} text-center`}>
                    <span className="inline-block font-display font-semibold text-body-sm uppercase tracking-wider text-neutral-500 mb-2">
                      {discipline.characterName}
                    </span>
                    <h3 className="font-display font-bold text-display-sm text-neutral-900 mb-4">
                      {discipline.name}
                    </h3>
                    <p className="font-body text-body-lg text-neutral-700 mb-6">
                      {details.tagline}
                    </p>

                    {/* Skills */}
                    <div className={`flex flex-wrap gap-2 ${isEven ? "md:justify-start" : "md:justify-end"} justify-center`}>
                      {details.skills.map((skill) => (
                        <span
                          key={skill}
                          className="inline-flex items-center gap-1 bg-white/80 rounded-full px-4 py-2 font-body text-body-sm text-neutral-700 shadow-sm"
                        >
                          <span className="text-meadow-500">✓</span>
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    </section>
  );
}

/** How it works section */
function HowItWorksSection() {
  const steps = [
    {
      step: 1,
      title: "Sign Up Free",
      description: "Create your parent account—no credit card needed.",
      image: "/images/icons/icon-signup.png",
    },
    {
      step: 2,
      title: "Create a Profile",
      description: "Add your child's name and pick an avatar. That's it.",
      image: "/images/icons/icon-profile.png",
    },
    {
      step: 3,
      title: "Start Playing",
      description: "Your child picks a character, starts an adventure, and learning begins.",
      image: "/images/icons/icon-play.png",
    },
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-4">
            Getting started takes{" "}
            <span className="text-magic-500">60 seconds</span>
          </h2>
        </div>

        <div className="relative">
          {/* Connector line - spans across all steps */}
          <div className="hidden md:block absolute top-16 left-[16.67%] right-[16.67%] h-1 bg-gradient-to-r from-sunshine-300 via-magic-300 to-meadow-300 rounded-full" />

          <div className="grid md:grid-cols-3 gap-8 relative">
            {steps.map((item) => (
              <div key={item.step} className="relative text-center group">
                {/* Illustrated icon */}
                <div className="relative w-32 h-32 mx-auto mb-6">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110 drop-shadow-lg"
                  />
                </div>

                <h3 className="font-display font-bold text-body-xl text-neutral-900 mb-2">
                  Step {item.step}: {item.title}
                </h3>
                <p className="font-body text-body-md text-neutral-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <Link to="/register">
            <Button size="lg" variant="primary">
              Start Free Now
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/** Parent trust section */
function ParentSection() {
  return (
    <section className="py-20 bg-gradient-to-b from-sky-50 to-white">
      <div className="max-w-5xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-6">
              You stay in control.
              <br />
              <span className="text-sky-600">They stay engaged.</span>
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12">
                  <img
                    src="/images/icons/icon-dashboard.png"
                    alt="Parent Dashboard"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="font-display font-bold text-body-lg text-neutral-900 mb-1">
                    Parent Dashboard
                  </h3>
                  <p className="font-body text-body-md text-neutral-600">
                    See exactly what your child is learning. Track stars, badges, and time
                    in each discipline.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12">
                  <img
                    src="/images/icons/icon-reports.png"
                    alt="Weekly Reports"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="font-display font-bold text-body-lg text-neutral-900 mb-1">
                    Weekly Reports
                  </h3>
                  <p className="font-body text-body-md text-neutral-600">
                    Receive progress summaries showing real learning outcomes—not just
                    playtime.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12">
                  <img
                    src="/images/icons/icon-privacy.png"
                    alt="Complete Privacy"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="font-display font-bold text-body-lg text-neutral-900 mb-1">
                    Complete Privacy
                  </h3>
                  <p className="font-body text-body-md text-neutral-600">
                    COPPA compliant. No data collection from children. No ads. No social
                    features.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust badge */}
          <div className="bg-white rounded-3xl p-8 shadow-playful-lg border-2 border-neutral-100">
            <div className="text-center mb-6">
              <img
                src="/images/icons/icon-promise.png"
                alt="JumpBean Promise"
                className="w-16 h-16 mx-auto mb-4 object-contain"
              />
              <h3 className="font-display font-bold text-body-xl text-neutral-900">
                The JumpBean Promise
              </h3>
            </div>

            <ul className="space-y-4">
              {[
                "No ads. Ever.",
                "No in-app purchases your child can find.",
                "No manipulation to make your 4-year-old beg for upgrades.",
                "All billing happens in your account—separate from play.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="text-meadow-500 text-lg flex-shrink-0">✓</span>
                  <span className="font-body text-body-md text-neutral-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Pricing section */
function PricingSection() {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-white to-neutral-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-display font-extrabold text-display-md text-magic-600 mb-4">
            Simple, Magical Pricing
          </h2>
          <p className="font-body text-body-lg text-neutral-600 mb-8">
            Start free and unlock more adventures when you're ready
          </p>

          {/* Billing toggle - matches /pricing page */}
          <div className="flex flex-col items-center gap-3">
            <div className="inline-flex items-center gap-1 p-1.5 bg-cloud rounded-full shadow-lg border-2 border-magic-100">
              <button
                onClick={() => setIsAnnual(false)}
                className={`
                  relative px-6 py-3 rounded-full font-display font-bold text-body-md transition-all duration-300
                  ${!isAnnual
                    ? "bg-magic-500 text-white shadow-md"
                    : "text-neutral-600 hover:text-magic-600"
                  }
                `}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`
                  relative px-6 py-3 rounded-full font-display font-bold text-body-md transition-all duration-300
                  ${isAnnual
                    ? "bg-magic-500 text-white shadow-md"
                    : "text-neutral-600 hover:text-magic-600"
                  }
                `}
              >
                Annual
                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-meadow-400 text-white text-xs font-bold rounded-full shadow-sm">
                  Save 20%
                </span>
              </button>
            </div>
            <p className="font-body text-body-sm text-meadow-600 font-medium">
              Most families choose annual
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {/* Free tier */}
          <div className="relative flex flex-col">
            <Card variant="elevated" className="flex-1 flex flex-col p-6 lg:p-8">
              <h3 className="font-display font-bold text-display-sm text-magic-600 mb-1">
                Free
              </h3>
              <p className="font-body text-body-md text-neutral-500 mb-6">
                Start the adventure
              </p>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-extrabold text-display-md text-neutral-900">
                    $0
                  </span>
                </div>
                <p className="mt-1 font-body text-body-sm text-neutral-500">
                  Free forever
                </p>
              </div>

              {/* Child limit */}
              <div className="flex items-center gap-2 mb-6 p-3 bg-fairy-50 rounded-xl">
                <ChildIcon className="w-5 h-5 text-fairy-500" />
                <span className="font-body text-body-md text-fairy-700">
                  1 child profile
                </span>
              </div>

              <ul className="flex-1 space-y-3 mb-8">
                {[
                  "Level 1 of all 5 disciplines",
                  "All magical character guides",
                  "Stars & badges for completed levels",
                  "Basic progress tracking",
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3 font-body text-body-md text-neutral-700">
                    <CheckIcon className="w-5 h-5 text-meadow-500 flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link to="/register" className="block">
                <Button variant="secondary" fullWidth size="lg">
                  Get Started Free
                </Button>
              </Link>
            </Card>
          </div>

          {/* Family tier - highlighted */}
          <div className="relative flex flex-col pt-4">
            {/* Popular badge */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="px-5 py-1.5 bg-gradient-to-r from-sunshine-400 via-sunshine-500 to-sunshine-400 text-neutral-900 font-display font-bold text-sm rounded-full shadow-lg border-2 border-sunshine-600">
                Most Popular
              </div>
            </div>

            <Card
              variant="elevated"
              className="flex-1 flex flex-col p-6 lg:p-8 border-2 border-magic-400 shadow-[0_8px_32px_rgba(155,89,182,0.2)]"
            >
              <h3 className="font-display font-bold text-display-sm text-magic-600 mb-1">
                Family
              </h3>
              <p className="font-body text-body-md text-neutral-500 mb-6">
                Unlock the full journey
              </p>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-extrabold text-display-md text-neutral-900">
                    ${isAnnual ? "4" : "5"}
                  </span>
                  <span className="font-body text-body-md text-neutral-500">
                    /month
                  </span>
                </div>
                {isAnnual && (
                  <p className="mt-1 font-body text-body-sm text-meadow-600">
                    $48 billed annually
                    <span className="ml-2 px-2 py-0.5 bg-meadow-100 text-meadow-700 rounded-full text-xs font-semibold">
                      Save 20%
                    </span>
                  </p>
                )}
              </div>

              {/* Child limit */}
              <div className="flex items-center gap-2 mb-6 p-3 bg-fairy-50 rounded-xl">
                <ChildIcon className="w-5 h-5 text-fairy-500" />
                <span className="font-body text-body-md text-fairy-700">
                  Up to 4 children
                </span>
              </div>

              <ul className="flex-1 space-y-3 mb-8">
                {[
                  "All 5 levels per discipline",
                  "Detailed progress reports",
                  "Parent dashboard",
                  "All badges & rewards",
                  "Priority support",
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3 font-body text-body-md text-neutral-700">
                    <CheckIcon className="w-5 h-5 text-meadow-500 flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link to="/pricing" className="block">
                <Button variant="primary" fullWidth size="lg">
                  Start Family Plan
                </Button>
              </Link>
            </Card>
          </div>

          {/* Classroom tier */}
          <div className="relative flex flex-col">
            <Card variant="elevated" className="flex-1 flex flex-col p-6 lg:p-8">
              <h3 className="font-display font-bold text-display-sm text-magic-600 mb-1">
                Classroom
              </h3>
              <p className="font-body text-body-md text-neutral-500 mb-6">
                For educators & schools
              </p>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-extrabold text-display-md text-neutral-900">
                    ${isAnnual ? "24.17" : "29"}
                  </span>
                  <span className="font-body text-body-md text-neutral-500">
                    /month
                  </span>
                </div>
                {isAnnual && (
                  <p className="mt-1 font-body text-body-sm text-meadow-600">
                    $290 billed annually
                    <span className="ml-2 px-2 py-0.5 bg-meadow-100 text-meadow-700 rounded-full text-xs font-semibold">
                      Save 17%
                    </span>
                  </p>
                )}
              </div>

              {/* Child limit */}
              <div className="flex items-center gap-2 mb-6 p-3 bg-fairy-50 rounded-xl">
                <ChildIcon className="w-5 h-5 text-fairy-500" />
                <span className="font-body text-body-md text-fairy-700">
                  Up to 35 students
                </span>
              </div>

              <ul className="flex-1 space-y-3 mb-8">
                {[
                  "Everything in Family",
                  "Class management tools",
                  "Student grouping",
                  "Exportable progress reports",
                  "Bulk student import",
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3 font-body text-body-md text-neutral-700">
                    <CheckIcon className="w-5 h-5 text-meadow-500 flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link to="/pricing" className="block">
                <Button variant="secondary" fullWidth size="lg">
                  Start Classroom Plan
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

/** FAQ Section */
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-20 bg-gradient-to-b from-neutral-50 to-white">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-display font-extrabold text-display-md text-neutral-900 mb-4">
            Questions? <span className="text-magic-500">Answers.</span>
          </h2>
        </div>

        <div className="space-y-4">
          {FAQ_DATA.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border-2 border-neutral-200 overflow-hidden shadow-playful"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
              >
                <span className="font-display font-bold text-body-lg text-neutral-900">
                  {item.question}
                </span>
                <span
                  className={`text-2xl text-magic-500 transition-transform duration-300 ${
                    openIndex === index ? "rotate-45" : ""
                  }`}
                >
                  +
                </span>
              </button>

              <div
                className={`
                  overflow-hidden transition-all duration-300
                  ${openIndex === index ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}
                `}
              >
                <p className="px-6 pb-5 font-body text-body-md text-neutral-600">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Final CTA section */
function FinalCTASection() {
  return (
    <section className="py-24 bg-gradient-to-b from-magic-100 via-sunshine-50 to-sky-100 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {HERO_PARTICLES.slice(0, 15).map((particle) => (
          <Particle key={particle.id} particle={particle} />
        ))}
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
        {/* Character row */}
        <div className="flex justify-center gap-4 mb-8">
          {(["math", "spelling", "patterns", "typing", "programming"] as DisciplineSlug[]).map(
            (discipline, index) => (
              <div
                key={discipline}
                className="animate-float"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <CharacterImage discipline={discipline} expression="happy" size="lg" />
              </div>
            )
          )}
        </div>

        <h2 className="font-display font-extrabold text-display-md md:text-display-lg text-neutral-900 mb-4">
          Ready to make learning{" "}
          <span className="text-magic-600">magical</span>?
        </h2>

        <p className="font-body text-body-lg text-neutral-600 mb-8">
          Start free. See the sparkle in their eyes.
        </p>

        <Link to="/register">
          <Button size="lg" variant="primary" className="text-xl px-12">
            Start Free Now
          </Button>
        </Link>

        <p className="mt-6 font-body text-body-sm text-neutral-500">
          No credit card required. Cancel anytime.
        </p>
      </div>
    </section>
  );
}

// ==================== Main Component ====================

/**
 * LandingPage - Conversion-focused homepage for JumpBean
 *
 * Features:
 * - Animated hero with floating particles and character showcase
 * - Problem/solution narrative flow
 * - Character and discipline showcases
 * - Clear pricing comparison
 * - FAQ with accordions
 * - Multiple CTAs throughout
 */
export function LandingPage() {
  usePageTitle("Screen Time That Teaches | JumpBean");

  return (
    <div className="flex flex-col">
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <CharacterSection />
      <DisciplinesSection />
      <HowItWorksSection />
      <ParentSection />
      <PricingSection />
      <FAQSection />
      <FinalCTASection />
    </div>
  );
}

export default LandingPage;
